/**
 * 문서 폴더 시드 — LIFETHINGS 엑셀의 문서 시트 다섯(03 설계업체List, 04 자재업체List, 05 인허가업무,
 * 07 법개정, 08 협의내용)을 폴더로 옮긴 모양이다. 칸 이름은 시트 머리글 그대로이고, 시트의 비고 ·
 * 검토착안점처럼 긴 글은 본문으로 간다. 업체와 담당자, 연락처는 지어낸 것이다 — 엑셀의 실제 연락처는
 * 옮기지 않는다(계좌번호 칸도 뺐다). 사람 id 는 data/staff.ts, 프로젝트 id 는 data/projects.ts 의 것이다.
 *
 * 순서(position)와 켜짐 여부는 규칙이 뻔해서 아래에서 채운다 — 리터럴마다 적지 않는다.
 */
import type { DocumentField, DocumentOption, DocumentDetail, DocumentFolder } from "@/types";

type FieldSeed = Omit<DocumentField, "position" | "is_active" | "options"> & {
  options: Omit<DocumentOption, "position">[];
};
type FolderSeed = Omit<DocumentFolder, "fields"> & { fields: FieldSeed[] };

const text = (id: string, name: string): FieldSeed => ({
  id,
  name,
  value_type: "text",
  options: [],
});

/** 업체 목록 두 폴더가 같이 쓰는 칸 — 시트 머리글의 담당자부터 업데이트 일자까지. */
const vendorFields = (prefix: string): FieldSeed[] => [
  text(`${prefix}-work`, "업무"),
  text(`${prefix}-contact`, "담당자"),
  text(`${prefix}-tel`, "TEL"),
  text(`${prefix}-hp`, "H.P"),
  { id: `${prefix}-email`, name: "E-MAIL", value_type: "url", options: [] },
  { id: `${prefix}-projects`, name: "진행 프로젝트", value_type: "project", options: [] },
  {
    id: `${prefix}-grade`,
    name: "평가",
    value_type: "select",
    options: [
      { id: `${prefix}-g-top`, name: "상", color: "green" },
      { id: `${prefix}-g-high`, name: "중상", color: "blue" },
      { id: `${prefix}-g-mid`, name: "중", color: "orange" },
      { id: `${prefix}-g-low`, name: "하", color: "red" },
    ],
  },
  { id: `${prefix}-updated`, name: "업데이트 일자", value_type: "date", options: [] },
];

const FOLDERS: FolderSeed[] = [
  {
    id: "t-vendors",
    slug: "vendors",
    name: "설계업체",
    icon: "buildings",
    color: "#3182f6",
    description: "구조 · 해체 · 토목 · 설비 등 협력 설계업체와 담당자",
    position: 10,
    is_active: true,
    fields: [
      {
        id: "dv-field",
        name: "분야",
        value_type: "select",
        options: [
          { id: "dv-f-structure", name: "구조", color: "blue" },
          { id: "dv-f-demolition", name: "해체", color: "red" },
          { id: "dv-f-civil", name: "토목", color: "gray" },
          { id: "dv-f-mep", name: "기계, 전기", color: "green" },
          { id: "dv-f-landscape", name: "조경", color: "green" },
          { id: "dv-f-lighting", name: "조명", color: "orange" },
        ],
      },
      ...vendorFields("dv"),
    ],
  },
  {
    id: "t-materials",
    slug: "materials",
    name: "자재업체",
    icon: "hammer",
    color: "#a234c7",
    description: "공종별 자재업체와 담당자",
    position: 20,
    is_active: true,
    fields: [
      {
        id: "mt-major",
        name: "대분류",
        value_type: "select",
        options: [
          { id: "mt-m-steel", name: "강구조공사", color: "gray" },
          { id: "mt-m-wood", name: "목공사", color: "orange" },
          { id: "mt-m-stone", name: "석공사", color: "blue" },
          { id: "mt-m-window", name: "창호 및 유리공사", color: "green" },
          { id: "mt-m-facade", name: "외벽공사", color: "red" },
        ],
      },
      text("mt-minor", "중분류"),
      ...vendorFields("mt"),
    ],
  },
  {
    id: "t-permits",
    slug: "permits",
    name: "인허가업무",
    icon: "clipboard",
    color: "#fe9800",
    description: "인허가 · 심의 · 협의가 언제 걸리고 얼마나 걸리는지",
    position: 30,
    is_active: true,
    fields: [
      {
        id: "pm-kind",
        name: "구분",
        value_type: "select",
        options: [
          { id: "pm-k-city", name: "도시·국토", color: "blue" },
          { id: "pm-k-view", name: "경관·디자인", color: "green" },
          { id: "pm-k-building", name: "건축법", color: "orange" },
        ],
      },
      text("pm-law", "법적 근거"),
      text("pm-trigger", "적용 Trigger (규모/용도/입지)"),
      text("pm-office", "주관/협의기관"),
      text("pm-before", "선행 업무"),
      text("pm-after", "후행 업무"),
      text("pm-period", "예상 소요기간"),
    ],
  },
  {
    id: "t-laws",
    slug: "laws",
    name: "법개정",
    icon: "books",
    color: "#18a5a5",
    description: "",
    position: 40,
    is_active: true,
    fields: [
      { id: "lw-date", name: "일시", value_type: "date", options: [] },
      {
        id: "lw-kind",
        name: "분류",
        value_type: "select",
        options: [
          { id: "lw-k-fire", name: "방화", color: "red" },
          { id: "lw-k-housing", name: "주택", color: "blue" },
          { id: "lw-k-parking", name: "주차", color: "gray" },
        ],
      },
      text("lw-notice", "관련 공고문"),
    ],
  },
  {
    id: "t-meetings",
    slug: "meetings",
    name: "협의내용",
    icon: "",
    color: "",
    description: "",
    position: 50,
    is_active: true,
    fields: [
      { id: "mg-date", name: "DATE", value_type: "date", options: [] },
      text("mg-party", "업체"),
      text("mg-question", "질 의"),
      text("mg-answer", "회 신"),
    ],
  },
];

export const folders: DocumentFolder[] = FOLDERS.map((folder) => ({
  ...folder,
  fields: folder.fields.map((field, index) => ({
    ...field,
    position: (index + 1) * 10,
    is_active: true,
    options: field.options.map((option, order) => ({ ...option, position: (order + 1) * 10 })),
  })),
}));

const at = "2026-10-21T09:00:00+09:00";

type DocumentSeed = Omit<
  DocumentDetail,
  "position" | "project_id" | "created_by" | "created_at" | "deleted_at"
>;

const doc = (
  id: string,
  folder_id: string,
  title: string,
  values: DocumentSeed["values"],
  body_md = "",
  updated_by = "u-owner"
): DocumentSeed => ({
  id,
  folder_id,
  title,
  values,
  body_md,
  excerpt: "",
  version: 1,
  updated_at: at,
  updated_by,
});

const DOCS: DocumentSeed[] = [
  doc(
    "r-v1",
    "t-vendors",
    "한결구조",
    {
      "dv-field": "dv-f-structure",
      "dv-work": "구조설계",
      "dv-contact": "정민호 소장",
      "dv-tel": "02-000-0000",
      "dv-hp": "010-0000-0000",
      "dv-email": "mailto:hangyeol@example.com",
      "dv-projects": ["p-2508", "p-2511"],
      "dv-grade": "dv-g-high",
      "dv-updated": "2026-01-12",
    },
    "## 비고\n\n- 견적 관련은 이사에게 따로 연락한다.\n- 지하층 기초 보강 검토를 맡는다."
  ),
  doc("r-v2", "t-vendors", "다온해체", {
    "dv-field": "dv-f-demolition",
    "dv-work": "해체심의",
    "dv-hp": "010-1111-1111",
    "dv-projects": ["p-2305"],
    "dv-grade": "dv-g-low",
  }),
  doc(
    "r-v3",
    "t-vendors",
    "누리설비엔지니어링",
    {
      "dv-field": "dv-f-mep",
      "dv-work": "기계 · 전기 설계",
      "dv-contact": "한지수 과장",
      "dv-tel": "02-222-2222",
      "dv-projects": ["p-2410"],
      "dv-grade": "dv-g-mid",
      "dv-updated": "2025-02-03",
    },
    "",
    "u-staff"
  ),
  doc("r-v4", "t-vendors", "숲결조경", {
    "dv-field": "dv-f-landscape",
    "dv-work": "조경설계",
    "dv-contact": "서은비 실장",
    "dv-projects": ["p-2601", "p-2506"],
  }),
  doc(
    "r-mt1",
    "t-materials",
    "한빛석재",
    {
      "mt-major": "mt-m-stone",
      "mt-minor": "석재",
      "mt-work": "석재판넬",
      "mt-contact": "안도현 이사",
      "mt-hp": "010-3333-3333",
      "mt-projects": ["p-2404"],
      "mt-grade": "mt-g-top",
    },
    "샘플은 사무소로 보내 준다. 제주 현장은 운송 일정을 2주 앞당겨 잡는다."
  ),
  doc("r-mt2", "t-materials", "대한데크", {
    "mt-major": "mt-m-steel",
    "mt-minor": "데크플레이트",
    "mt-work": "데크플레이트, 스피드데크",
  }),
  doc("r-mt3", "t-materials", "온유창호", {
    "mt-major": "mt-m-window",
    "mt-minor": "곡면 창호",
    "mt-work": "알루미늄 창호, 곡면 프레임",
    "mt-contact": "손미래 수석",
    "mt-projects": ["p-2503"],
    "mt-grade": "mt-g-high",
    "mt-updated": "2026-08-20",
  }),
  doc(
    "r-p1",
    "t-permits",
    "경관심의",
    {
      "pm-kind": "pm-k-view",
      "pm-law": "경관법 제26조",
      "pm-trigger": "지자체 조례별 구역/규모 (서울: 10층 이상 등)",
      "pm-office": "지자체 경관위",
      "pm-before": "건축기획 및 기본설계안 확정",
      "pm-after": "건축심의 / 건축허가",
      "pm-period": "1~2개월",
    },
    "## PM 리스크 및 검토착안점\n\n입면 규제로 디자인 변경 압박이 온다. 대안 입면을 미리 준비해 심의 기간을 줄인다.",
    "u-pm"
  ),
  doc(
    "r-p2",
    "t-permits",
    "건축심의",
    {
      "pm-kind": "pm-k-building",
      "pm-law": "건축법 제4조의2",
      "pm-trigger": "다중이용건축물, 16층 이상 또는 분양 건축물 등",
      "pm-office": "지자체 건축위",
      "pm-before": "교통/경관심의 완료",
      "pm-after": "건축허가 신청",
      "pm-period": "1~2개월",
    },
    "## PM 리스크 및 검토착안점\n\n심의가 보류되면 설계 기간이 길어진다. 일정에 한 번 더 상정할 여유를 둔다.",
    "u-pm"
  ),
  doc("r-p3", "t-permits", "개발행위허가", {
    "pm-kind": "pm-k-city",
    "pm-law": "국토계획법 제56조",
    "pm-trigger": "녹지/관리지역 등 내 토지 형질변경 및 건축물 건축",
    "pm-office": "지자체 (도시계획과)",
    "pm-before": "지적측량, 토지이용계획 확정",
    "pm-after": "건축허가 접수",
    "pm-period": "1~2개월",
  }),
  doc(
    "r-l1",
    "t-laws",
    "아파트 지하주차장 높이",
    {
      "lw-date": "2018-06-19",
      "lw-kind": "lw-k-parking",
      "lw-notice": "국토교통부 보도자료",
    },
    "## 알아두어야 하는 내용\n\n지상공원형 아파트의 지하주차장 층 높이 기준이 2.7m 이상으로 올라갔다."
  ),
  doc(
    "r-l2",
    "t-laws",
    "서울시 다락 설치기준",
    { "lw-date": "2019-04-16", "lw-kind": "lw-k-housing" },
    "적용하더라도 구청마다 별도 기준이 더 있으니 해당 지자체에 한 번 더 확인한다.",
    "변경민"
  ),
  doc(
    "r-m1",
    "t-meetings",
    "곡률유리",
    {
      "mg-date": "2026-08-17",
      "mg-party": "온유창호 (손미래 수석)",
      "mg-question": "평면 곡률을 적용할 수 있는 유리 두께",
      "mg-answer": "복층유리 T28(6+16+6) 또는 T32(8+16+8)",
    },
    "",
    "u-staff"
  ),
  doc("r-m2", "t-meetings", "보험 발행", {
    "mg-date": "2026-08-12",
    "mg-party": "건축사공제조합",
    "mg-question": "설계 보증보험 발행 절차",
    "mg-answer": "공제조합 누리집에서 신청",
  }),
];

/** 문서 — 만든 사람과 순서를 채운다. 프로젝트에 매단 문서는 아직 없다. */
export const documents: DocumentDetail[] = DOCS.map((row, index) => ({
  ...row,
  project_id: null,
  position: (index + 1) * 10,
  created_by: row.updated_by ?? "u-owner",
  created_at: at,
  deleted_at: null,
}));
